import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Clock, User, Tag } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { sanitizeHtml } from '../lib/sanitize';
import axios from 'axios';
import { useSeoHead, siteUrl } from '../components/website/SeoHead';
import { brandName } from '../hooks/useCompanyBrand';

const seoCore = require('../seo/seoCore');

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = `Blog | ${brandName()} - Window Treatment Tips & News`;
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [postsRes, catsRes] = await Promise.all([
          axios.get(`${API_URL}/blog/posts`, {
            params: selectedCategory ? { category: selectedCategory } : {}
          }),
          axios.get(`${API_URL}/blog/categories`)
        ]);
        setPosts(postsRes.data.posts || []);
        setCategories(catsRes.data || []);
      } catch (error) {
        console.error('Failed to fetch blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCategory]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-20 bg-neutral-50">
        <div className="container-xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl">
            <span className="label-overline text-amber-700">Insights & Inspiration</span>
            <h1 className="mt-4 text-neutral-900">Apollo Shades Blog</h1>
            <p className="mt-6 text-xl text-neutral-600 leading-relaxed">
              Expert tips, design inspiration, and the latest news in window treatments and smart home technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Blog Content */}
      <section className="section-padding">
        <div className="container-xl">
          <div className="grid lg:grid-cols-4 gap-12">
            {/* Sidebar */}
            <div className="lg:col-span-1 order-2 lg:order-1">
              <div className="sticky top-32">
                <h3 className="text-sm font-semibold text-neutral-400 uppercase tracking-wider mb-4">Categories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${!selectedCategory ? 'bg-amber-100 text-amber-800 font-medium' : 'hover:bg-neutral-100 text-neutral-600'}`}
                    data-testid="blog-category-all"
                  >
                    All Posts
                  </button>
                  {categories.map((cat) => (
                    <button
                      key={cat}
                      onClick={() => setSelectedCategory(cat)}
                      className={`block w-full text-left px-3 py-2 text-sm rounded transition-colors ${selectedCategory === cat ? 'bg-amber-100 text-amber-800 font-medium' : 'hover:bg-neutral-100 text-neutral-600'}`}
                      data-testid={`blog-category-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Posts Grid */}
            <div className="lg:col-span-3 order-1 lg:order-2">
              {loading ? (
                <div className="grid md:grid-cols-2 gap-8">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="animate-pulse">
                      <div className="aspect-[16/9] bg-neutral-200 mb-4" />
                      <div className="h-4 bg-neutral-200 rounded w-1/4 mb-2" />
                      <div className="h-6 bg-neutral-200 rounded w-3/4 mb-2" />
                      <div className="h-4 bg-neutral-200 rounded w-full" />
                    </div>
                  ))}
                </div>
              ) : posts.length === 0 ? (
                <div className="text-center py-16 bg-neutral-50 rounded-lg" data-testid="blog-empty-state">
                  <h3 className="text-lg font-medium mb-2">No posts yet</h3>
                  <p className="text-neutral-600">Check back soon for new content!</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 gap-8" data-testid="blog-posts-grid">
                  {posts.map((post, index) => (
                    <motion.article
                      key={post.slug}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="group"
                      data-testid={`blog-post-${post.slug}`}
                    >
                      <Link to={`/blog/${post.slug}`}>
                        {post.featured_image ? (
                          <div className="aspect-[16/9] overflow-hidden bg-neutral-100 mb-4">
                            <img
                              src={post.featured_image}
                              alt={post.featured_image_alt || post.title}
                              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            />
                          </div>
                        ) : (
                          <div className="aspect-[16/9] bg-gradient-to-br from-neutral-100 to-neutral-200 mb-4 flex items-center justify-center">
                            <span className="text-neutral-400 text-sm">No image</span>
                          </div>
                        )}
                        <div className="flex items-center gap-4 text-sm text-neutral-500 mb-2">
                          <span className="text-amber-700 font-medium">{post.category}</span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(post.published_at)}
                          </span>
                        </div>
                        <h2 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-amber-700 transition-colors">
                          {post.title}
                        </h2>
                        <p className="text-neutral-600 text-sm line-clamp-2 mb-3">
                          {post.excerpt}
                        </p>
                        <span className="text-sm font-medium text-neutral-900 flex items-center gap-1 group-hover:gap-2 transition-all">
                          Read More <ArrowRight className="w-4 h-4" />
                        </span>
                      </Link>
                    </motion.article>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API_URL}/blog/posts/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Failed to fetch post:', error);
        setError('Post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const postSeo = React.useMemo(() => {
    if (!post) return null;
    const e = seoCore.blogEntry(post);
    return { title: e.title, meta_description: e.description, og_image: e.image, canonical_url: `${siteUrl()}/blog/${slug}`, schema_markup: seoCore.seoFor(`/blog/${slug}`, siteUrl(), e).schema };
  }, [post, slug]);
  useSeoHead(postSeo, { path: `/blog/${slug}` });

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
        </div>
      </Layout>
    );
  }

  if (error || !post) {
    return (
      <Layout>
        <div className="min-h-screen flex flex-col items-center justify-center">
          <h1 className="text-2xl font-semibold mb-4">Post not found</h1>
          <Link to="/blog" className="text-amber-700 hover:underline">Back to Blog</Link>
        </div>
      </Layout>
    );
  }

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="pt-32 pb-12">
        <div className="container-lg">
          <Link to="/blog" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 mb-8 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Blog
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="label-overline text-amber-700">{post.category}</span>
            <h1 className="mt-4 text-neutral-900">{post.title}</h1>
            <div className="flex flex-wrap items-center gap-4 mt-6 text-neutral-500">
              <span className="flex items-center gap-2">
                <User className="w-4 h-4" />
                {post.author}
              </span>
              <span className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at)}
              </span>
              {post.tags?.length > 0 && (
                <span className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  {post.tags.join(', ')}
                </span>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      {post.featured_image && (
        <section className="container-lg mb-12">
          <img 
            src={post.featured_image} 
            alt={post.featured_image_alt || post.title} 
            className="w-full aspect-[2/1] object-cover" 
          />
        </section>
      )}

      {/* Content */}
      <section className="container-lg pb-20">
        <div className="max-w-3xl mx-auto">
          {post.excerpt && (
            <p className="text-xl text-neutral-600 leading-relaxed mb-8 font-medium">
              {post.excerpt}
            </p>
          )}
          <div 
            className="prose prose-lg prose-neutral max-w-none"
            dangerouslySetInnerHTML={{ __html: sanitizeHtml(post.content.replace(/\n/g, '<br/>')) }}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-neutral-50">
        <div className="container-xl text-center">
          <h2 className="mb-6">Ready to Start Your Project?</h2>
          <p className="text-neutral-600 max-w-2xl mx-auto mb-8">
            Get in touch with our team to discuss your window treatment needs and receive a custom quote.
          </p>
          <Link to="/quote" className="btn-primary inline-flex items-center">Request a Quote</Link>
        </div>
      </section>
    </Layout>
  );
};
